/* eslint-disable @typescript-eslint/no-explicit-any */

import { v1 as uuid } from 'uuid';

import snakeCase from 'lodash/snakeCase';
import isPlainObject from 'lodash/isPlainObject';

type Params = {
  [key in string | number]: any;
};

interface FrameRendererParams {
  link: string;
  target: Element;
  listener?: MessageListener;
}

interface MessageParams {
  code: string;
  params?: Params;
}

interface MessageListener {
  (event: MessageListenerEvent): void;
}

interface MessageListenerEvent extends MessageParams {
  original: MessageEvent;
}

export default class FrameRenderer {
  private readonly _url: URL;

  private readonly _name: string;

  private readonly _target: Element;

  private readonly _listener?: MessageListener;

  private _frame?: HTMLIFrameElement;

  constructor({ link, target, listener }: FrameRendererParams) {
    this._url = new URL(link);
    this._target = target;
    this._listener = listener;

    this._name = uuid();
  }

  render(frameGetParams: Params | null, framePostParams: Params | null) {
    const form = this._generatePostForm(frameGetParams, framePostParams);
    const frame = this._generateFrame();

    this._replaceTargetContent(form, frame);

    this._frame = frame;

    this._attachMessageEvent();

    form.submit();
  }

  postMessageToFrame(message: MessageParams): void {
    if (!this._frame) {
      throw new TypeError('Frame is not rendered yet');
    }

    const { contentWindow } = this._frame;

    if (!contentWindow) {
      throw new Error('No access to frame contentWindow');
    }

    contentWindow.postMessage(message, this._url.origin);
  }

  private _generatePostForm(
    frameGetParams: Params | null,
    framePostParams: Params | null
  ): HTMLFormElement {
    const form = document.createElement('form');
    const inputs = this._buildParamInputs(framePostParams || {});

    form.style.display = 'none';

    form.method = 'post';
    form.target = this._name;
    form.action = this._buildURL(this._url.href, frameGetParams || {});

    form.append(...inputs);

    return form;
  }

  private _generateFrame(): HTMLIFrameElement {
    const frame = document.createElement('iframe');

    frame.name = this._name;

    frame.width = '100%';
    frame.height = '100%';
    frame.allowFullscreen = true;

    return frame;
  }

  private _buildParamInputs(params: object): Array<HTMLInputElement> {
    return (
      Object.entries(params)
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        .filter(([_, value]) => typeof value !== 'undefined')
        .map(
          ([name, value]): HTMLInputElement => {
            const input = document.createElement('input');

            input.name = snakeCase(name);
            input.value = value as string;
            input.hidden = true;

            return input;
          }
        )
    );
  }

  _attachMessageEvent() {
    if (!this._listener) {
      return;
    }

    window.addEventListener('message', (original: MessageEvent) => {
      if (!this._listener) {
        return;
      }

      const { origin, data: message } = original;

      if (this._url.origin !== origin || !isPlainObject(message)) {
        return;
      }

      this._listener({ ...message, original });
    });
  }

  private _replaceTargetContent(...elements: Array<Element>): void {
    this._removeChildren(this._target);

    this._target.append(...elements);
  }

  private _removeChildren(element: Element): void {
    while (element.firstChild) {
      element.removeChild(element.lastChild as Node);
    }
  }

  private _buildURL(origin: string, params = {}): string {
    const url = new URL(origin);

    url.search = new URLSearchParams(params).toString();

    return url.toString();
  }
}

export { Params, MessageListenerEvent };
